import React from 'react';
import { Box } from '@mui/material';
import { useTheme, useMediaQuery } from '@mui/material';
import DesktopNav from './DesktopNav';
import DesktopPlaceholder from './DesktopPlaceholder';
import { useResizableWidth } from '../hooks/useResizableWidth';

/**
 * DESKTOP LAYOUT — WhatsApp Web pattern (≥960px only)
 *
 * mode="chats"  → DesktopNav (64px) + resizable chat list + conversation panel (flex)
 * mode="page"   → DesktopNav (64px) + scrollable content area (max-width 800px centered)
 *
 * On mobile: transparent pass-through — renders children with zero wrapping.
 */
const DesktopLayout = ({ mode = 'page', listContent, detailContent, children }) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  // Called unconditionally (rules of hooks) — value is ignored on mobile.
  const { width: listWidth, isDragging, startDrag } = useResizableWidth();

  // On mobile: transparent pass-through, zero layout impact
  if (!isDesktop) {
    return <>{children ?? listContent}</>;
  }

  if (mode === 'chats') {
    return (
      <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
        {/* Vertical nav sidebar */}
        <DesktopNav />

        {/* Chat list panel — overflowY:auto so sticky TopBar works and list scrolls */}
        <Box
          sx={{
            width: listWidth,
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column',
            height: '100vh',
            overflowY: 'auto',
            overflowX: 'hidden',
          }}
        >
          {listContent}
        </Box>

        {/* Resize handle — drag to adjust the list panel width */}
        <Box
          onPointerDown={startDrag}
          sx={{
            width: '6px',
            flexShrink: 0,
            cursor: 'col-resize',
            touchAction: 'none',
            position: 'relative',
            bgcolor: isDragging ? 'primary.main' : 'divider',
            transition: 'background-color 0.15s ease',
            '&:hover': { bgcolor: 'primary.main' },
            // Widen the hit area without affecting layout
            '&::after': {
              content: '""',
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: '-3px',
              right: '-3px',
            },
          }}
        />

        {/* Conversation / detail panel */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            height: '100vh',
            overflowY: 'auto',
            overflowX: 'hidden',
          }}
        >
          {detailContent ?? <DesktopPlaceholder />}
        </Box>
      </Box>
    );
  }

  // mode === 'page': sidebar nav + centered scrollable content
  return (
    <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
      {/* Vertical nav sidebar */}
      <DesktopNav />

      {/* Content area — scrollable, centered */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
          overflowY: 'auto',
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default DesktopLayout;
